import { Component, OnInit, inject } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute } from "@angular/router";
import GridsConfig from "@app/common/grid.config";
import {
  Migration,
  MigrationStatisticsRow,
  MigrationExecutionStep,
  MigrationExecutionStoredProcedure,
} from "@app/interface/interfaces";
import { PaginatorFilter } from "@app/models/grids";
import { ApiService } from "@app/services/api.service";
import { AuthService } from "@app/auth/auth.service";
import { MigrationService } from "@app/services/migration.service";
import { MigrationStore } from "@app/services/migration.store";
import { ColumnSortConfig } from "@app/components/table-grid/table-grid/table-grid.component";
import { catchError, map, of, tap } from "rxjs";
import { Chart, ChartConfiguration, registerables, TooltipItem } from "chart.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { F } from "@angular/cdk/scrolling-module.d-ud2XrbF8";

Chart.register(...registerables);

type TimelineBarValue = [number, number];

interface TimelineChartItem {
  label: string;
  companyName: string;
  migrationId: number;
  startHour: number;
  endHour: number;
  durationMs: number;
  startedLabel: string;
  endedLabel: string;
  hasStatistics: boolean;
}

@Component({
  selector: "app-statistics",
  templateUrl: "./statistics.component.html",
  styleUrls: ["./statistics.component.scss"],
  standalone: false,
})
export class StatisticsComponent implements OnInit {
  private pendingQueryMigrationId: number | null = null;
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(ApiService);
  private readonly migrationStore = inject(MigrationStore);
  private readonly migrationService = inject(MigrationService);
  readonly authService = inject(AuthService);

  hasAdminGroup$ = this.authService.hasAdminGroup$;

  migrations: Migration[] = [];
  selectedMigrationId: number | null = null;
  migrationId: number | null = null;
  migration: Migration | null = null;
  activeTabIndex = 0;
  loading = false;
  loadingMigrations = false;
  error = false;
  statisticsLoadCompleted = false;
  timelineViewEnabled = false;
  selectedTimelineStartDate: Date = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);
  selectedTimelineEndDate: Date = new Date();
  startTimeHours: number = this.selectedTimelineStartDate.getHours();
  endTimeHours: number = this.selectedTimelineEndDate.getHours();
  selectedTimelineRangePreset: string | null = 'last24h';
  timelineMigrations: Migration[] = [];
  timelineChartItems: TimelineChartItem[] = [];

  statisticsColumns = GridsConfig.MigrationStatistics.Columns;
  statisticsTableHeaders = GridsConfig.MigrationStatistics.Headers;
  statisticsColumnWidths = GridsConfig.MigrationStatistics.Widths;
  statisticsRows: MigrationStatisticsRow[] = [];
  statisticsRowsPaginated: MigrationStatisticsRow[] = [];
  nameFilterOptions: string[] = [];
  selectedNameFilter: string | null = null;
  protocolIdFilterOptions: number[] = [];
  selectedProtocolIdFilter: number | null = null;
  migrationsViewMode: "table" | "diagram" = "table";
  deletedMigrationsViewMode: "table" | "diagram" = "table";
  initializingMigrationDuration: number | null = null;

  get initializingMigrationDurationText(): string {
    if (this.initializingMigrationDuration === null) return "";
    return this.migrationService.getDurationText(this.initializingMigrationDuration);
  }
  statisticsPaginatorFilter: PaginatorFilter = new PaginatorFilter({
    pageSize: 10,
  });

  // Diagram/Timeline chart data
  diagramChartData: Array<{
    name: string;
    value: number;
    description: string;
  }> = [];
  barChartType: "bar" = "bar";
  barChartData: ChartConfiguration<"bar">["data"] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: "Duration (s)",
        backgroundColor: "#3b82f6",
        borderRadius: 6,
        borderWidth: 0,
      },
    ],
  };
  barChartOptions: ChartConfiguration<"bar">["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label
              ? `${context.dataset.label.replace("(s)", "").trim()}: `
              : "Duration: ";
            const rawDurationMs =
              this.diagramChartData[context.dataIndex]?.value ?? 0;
            const valueLabel = `${datasetLabel}${this.migrationService.getDurationText(rawDurationMs)}`;
            const description =
              this.diagramChartData[context.dataIndex]?.description?.trim() ||
              "";

            if (!description) {
              return valueLabel;
            }

            return [valueLabel, `Description: ${description}`];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Duration (s)",
        },
      },
      y: {
        title: {
          display: true,
          text: "Migration Stages",
        },
      },
    },
  };

  timelineBarChartType: "bar" = "bar";
  timelineBarChartData: ChartConfiguration<"bar", TimelineBarValue[], string>["data"] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: "Migration window",
        backgroundColor: "#1670FE",
        borderColor: "#0052CC",
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false,
        barThickness: 18,
      },
    ],
  };
  timelineBarChartOptions: ChartConfiguration<"bar", TimelineBarValue[], string>["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<"bar">[]) => {
            const item = this.timelineChartItems[items[0]?.dataIndex ?? -1];
            return item ? `${item.companyName} (#${item.migrationId})` : "Migration";
          },
          label: (context: TooltipItem<"bar">) => {
            const item = this.timelineChartItems[context.dataIndex];
            if (!item) {
              return "";
            }

            if (!item.hasStatistics) {
              return [
                `Started: ${item.startedLabel}`,
                `Ended: ${item.endedLabel}`,
                "No execution statistics available",
              ];
            }

            return [
              `Started: ${item.startedLabel}`,
              `Ended: ${item.endedLabel}`,
              `Duration: ${this.migrationService.getDurationText(item.durationMs)}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 24,
        title: {
          display: true,
          text: "Hour of day",
        },
        ticks: {
          stepSize: 1,
          callback: (value) => `${value}:00`,
        },
      },
      y: {
        title: {
          display: true,
          text: "Migrations",
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  get timelineChartHeight(): number {
    const minHeight = 460;
    const rowHeight = 20;
    return Math.max(minHeight, this.timelineChartItems.length * rowHeight);
  }

  /** Column type configuration for type-aware sorting */
  readonly columnSortTypes: ColumnSortConfig = {
    name: "string",
    protocol_id: "number",
    status: "string",
    started: "date",
    ended: "date",
    durationText: "string",
    description: "string",
  };

  /** Current sort state */
  currentSort: Sort = { active: "started", direction: "asc" };

  get isCustomTimelineRange(): boolean {
    return this.selectedTimelineRangePreset === null;
  }

  ngOnInit(): void {
    this.selectedMigrationId = null;
    this.onTimelineRangePresetChanged(this.selectedTimelineRangePreset);

    this.route.queryParamMap.subscribe((params) => {
      const queryMigrationId = params.get("migrationId");
      if (queryMigrationId && !Number.isNaN(Number(queryMigrationId))) {
        this.pendingQueryMigrationId = Number(queryMigrationId);
      } else {
        this.pendingQueryMigrationId = null;
      }

      this.loadMigrations();
    });
  }

  onTimelineViewChanged(enabled: boolean): void {
    this.timelineViewEnabled = enabled;

    if (enabled) {
      this.activeTabIndex = 0;
      this.refreshTimelineView();
    }
  }

  onTimelineDateRangeChanged(): void {
    // Apply time values to dates
    this.selectedTimelineStartDate.setHours(
      Math.min(23, Math.max(0, this.startTimeHours || 0)),
      0
    );
    this.selectedTimelineEndDate.setHours(
      Math.min(23, Math.max(0, this.endTimeHours || 0)),
      0
    );

    // Ensure start date is before end date
    if (this.selectedTimelineStartDate && this.selectedTimelineEndDate) {
      if (this.selectedTimelineStartDate > this.selectedTimelineEndDate) {
        const temp = this.selectedTimelineStartDate;
        this.selectedTimelineStartDate = this.selectedTimelineEndDate;
        this.selectedTimelineEndDate = temp;
        // Swap times as well
        const tempHours = this.startTimeHours;
        this.startTimeHours = this.endTimeHours;
        this.endTimeHours = tempHours;
      }
    }
    // Clear the preset when manually adjusting
    this.selectedTimelineRangePreset = null;
    this.refreshTimelineView();
  }

  onTimelineRangePresetChanged(preset: string | null): void {
    this.selectedTimelineRangePreset = preset;

    if (!preset) {
      return;
    }

    const now = new Date();
    const start = new Date();

    switch (preset) {
      case "last24h":
        start.setDate(start.getDate() - 1);
        break;
      case "last2days":
        start.setDate(start.getDate() - 2);
        break;
      case "lastweek":
        start.setDate(start.getDate() - 7);
        break;
    }

    start.setHours(0, 0, 0, 0);
    now.setHours(23, 0, 0, 0);

    this.selectedTimelineStartDate = start;
    this.selectedTimelineEndDate = now;
    this.startTimeHours = start.getHours();
    this.endTimeHours = now.getHours();
    this.refreshTimelineView();
  }

  get selectedTimelineDateRangeLabel(): string {
    const startLabel = this.selectedTimelineStartDate.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const endLabel = this.selectedTimelineEndDate.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${startLabel} - ${endLabel}`;
  }

  paginateStatistics(event: PageEvent): void {
    this.statisticsPaginatorFilter.currentPage = event.pageIndex;
    this.statisticsPaginatorFilter.pageSize = event.pageSize;
    this.applyStatisticsPagination();
  }

  /** Handle sort changes from table-grid */
  onSortChanged(sort: Sort): void {
    this.currentSort = sort;
    this.statisticsPaginatorFilter.currentPage = 0; // Reset to first page on sort change
    this.applyStatisticsPagination();
  }

  onNameFilterChanged(): void {
    this.statisticsPaginatorFilter.currentPage = 0;
    this.applyStatisticsPagination();
  }

  onProtocolIdFilterChanged(): void {
    this.statisticsPaginatorFilter.currentPage = 0;
    this.applyStatisticsPagination();
    this.updateDiagramChart();
  }

  onMigrationsViewToggle(checked: boolean): void {
    this.migrationsViewMode = checked ? "diagram" : "table";
    if (checked) {
      this.selectedNameFilter = null;
    }
    this.statisticsPaginatorFilter.currentPage = 0;
    this.applyStatisticsPagination();
    this.updateDiagramChart();
  }

  onDeletedMigrationsViewToggle(checked: boolean): void {
    this.deletedMigrationsViewMode = checked ? "diagram" : "table";
    if (checked) {
      this.selectedNameFilter = null;
    }
    this.statisticsPaginatorFilter.currentPage = 0;
    this.applyStatisticsPagination();
    this.updateDiagramChart();
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.activeTabIndex = event.index;
    this.resetSelectedMigrationState();

    if (this.activeTabIndex === 1) {
      this.loadDeletedMigrations();
      return;
    }

    this.loadMigrations();
  }

  onMigrationSelectionChanged(): void {
    if (!this.selectedMigrationId) {
      this.migrationId = null;
      this.migration = null;
      this.statisticsRows = [];
      this.statisticsRowsPaginated = [];
      this.nameFilterOptions = [];
      this.selectedNameFilter = null;
      this.protocolIdFilterOptions = [];
      this.selectedProtocolIdFilter = null;
      this.statisticsPaginatorFilter.count = 0;
      this.statisticsLoadCompleted = false;
      return;
    }

    this.migrationId = this.selectedMigrationId;
    this.migrationStore.setCurrentMigrationId(String(this.migrationId));
    this.loadMigrationStatistics(this.migrationId);
  }

  exportStatistics(): void {
    if (this.statisticsRows.length === 0 || !this.migrationId) {
      return;
    }

    this.exportStatisticsPdf();
  }

  private async exportStatisticsPdf(): Promise<void> {
    const filename = `migration-statistics-${this.migrationId}-${this.migration?.company_name}-${new Date().getTime()}`;
    const migrationName = this.migration?.company_name || "N/A";
    const exportRows = this.getSortedStatistics();

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    doc.setFontSize(16);
    doc.text(`Migration Statistics - ${migrationName}`, 40, 40);
    doc.setFontSize(10);
    doc.text(`Migration ID: ${this.migrationId}`, 40, 58);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 72);

    autoTable(doc, {
      startY: 88,
      head: [this.statisticsTableHeaders],
      body: exportRows.map((row) =>
        this.statisticsColumns.map((column) =>
          this.getExportCellValue(
            row as unknown as Record<string, unknown>,
            column,
          ),
        ),
      ),
      styles: {
        fontSize: 8,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [8, 53, 102],
      },
      margin: {
        left: 30,
        right: 30,
      },
      theme: "striped",
    });

    doc.addPage("a4", "landscape");
    doc.setFontSize(16);
    doc.text("Migration Timeline Chart", 40, 40);

    this.updateDiagramChart();
    const chartImage = await this.createChartImageForPdf();

    if (chartImage) {
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const imageWidth = pageWidth - margin * 2;
      const imageHeight = pageHeight - 100;

      doc.addImage(chartImage, "PNG", margin, 60, imageWidth, imageHeight);
    } else {
      doc.setFontSize(11);
      doc.text("No chart data is available for this migration.", 40, 72);
    }

    doc.save(`${filename}.pdf`);
  }

  private async createChartImageForPdf(): Promise<string | null> {
    if (!this.diagramChartData || this.diagramChartData.length === 0) {
      return null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 1600;
    canvas.height = 900;

    const context = canvas.getContext("2d");
    if (!context) {
      return null;
    }

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);

    const chartOptions: ChartConfiguration<"bar">["options"] = {
      ...this.barChartOptions,
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
    };

    const chart = new Chart(context, {
      type: this.barChartType,
      data: this.barChartData,
      options: chartOptions,
    });

    chart.update();
    await new Promise((resolve) => setTimeout(resolve, 50));

    const image = canvas.toDataURL("image/png");
    chart.destroy();
    return image;
  }

  exportTimelineChart(): void {
    if (this.timelineChartItems.length === 0) {
      return;
    }

    this.exportTimelinePdf();
  }

  private async exportTimelinePdf(): Promise<void> {
    const filename = `migration-timeline-${this.selectedTimelineStartDate.toISOString().split("T")[0]}-${new Date().getTime()}`;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    doc.setFontSize(16);
    doc.text("Migration Timeline Chart", 40, 40);
    doc.setFontSize(10);
    doc.text(`Date: ${this.selectedTimelineDateRangeLabel}`, 40, 58);
    doc.text(`Migrations: ${this.timelineMigrations.length}`, 40, 72);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 86);

    const timelineChartImage = await this.createTimelineChartImageForPdf();

    if (timelineChartImage) {
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const imageWidth = pageWidth - margin * 2;
      const imageHeight = pageHeight - 120;

      doc.addImage(timelineChartImage, "PNG", margin, 110, imageWidth, imageHeight);
    } else {
      doc.setFontSize(11);
      doc.text("No chart data is available for the selected date.", 40, 110);
    }

    doc.save(`${filename}.pdf`);
  }

  private async createTimelineChartImageForPdf(): Promise<string | null> {
    if (!this.timelineBarChartData || this.timelineChartItems.length === 0) {
      return null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 1600;
    canvas.height = Math.max(900, this.timelineChartItems.length * 32 + 100);

    const context = canvas.getContext("2d");
    if (!context) {
      return null;
    }

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);

    const chartOptions: ChartConfiguration<"bar">["options"] = {
      ...this.timelineBarChartOptions,
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
    };

    const chart = new Chart(context, {
      type: this.timelineBarChartType,
      data: this.timelineBarChartData,
      options: chartOptions,
    });

    chart.update();
    await new Promise((resolve) => setTimeout(resolve, 50));

    const image = canvas.toDataURL("image/png");
    chart.destroy();
    return image;
  }

  private getExportCellValue(
    row: Record<string, unknown>,
    column: string,
  ): string {
    const value = row[column];

    if (value === null || value === undefined) {
      return "";
    }

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return String(value);
    }

    return JSON.stringify(value);
  }

  private loadMigrations(): void {
    this.changeLoadingState(true);

    console.log("[Statistics] Loading migrations...");

    this.apiService
      .getMigrations()
      .pipe(
        tap((response: Migration[]) => {
          console.log(
            "[Statistics] Got migrations response:",
            response.length,
            "items",
          );
        }),
        map((migrations: Migration[]) =>
          migrations.map((migration) =>
            this.migrationService.processMigrationRecord(migration,false),
          ),
        ),
        tap((processedMigrations: Migration[]) => {
          console.log(
            "[Statistics] Processed migrations:",
            processedMigrations.length,
            "items",
          );
          this.migrations = processedMigrations.sort(
            (a, b) => (b.id || 0) - (a.id || 0),
          );
          this.refreshTimelineView();
          this.tryApplyPendingSelection();
          this.changeLoadingState(false);
        }),
        catchError((error) => {
          console.error("[Statistics] Error loading migrations:", error);
          this.migrations = [];
          this.refreshTimelineView();
          this.changeLoadingState(false);
          return of([]);
        }),
      )
      .subscribe((result) => {
        console.log(
          "[Statistics] Subscribe completed with",
          result?.length || 0,
          "migrations",
        );
      });
  }

  private loadDeletedMigrations(): void {
    this.changeLoadingState(true);

    console.log("[Statistics] Loading deleted migrations...");

    this.apiService
      .getDeletedMigrations()
      .pipe(
        tap((response: Migration[]) => {
          console.log(
            "[Statistics] Got deleted migrations response:",
            response.length,
            "items",
          );
        }),
        map((migrations: Migration[]) =>
          migrations.map((migration) =>
            this.migrationService.processMigrationRecord(migration),
          ),
        ),
        tap((processedMigrations: Migration[]) => {
          console.log(
            "[Statistics] Processed deleted migrations:",
            processedMigrations.length,
            "items",
          );
          this.migrations = processedMigrations.sort(
            (a, b) => (b.id || 0) - (a.id || 0),
          );
          this.refreshTimelineView();
          this.changeLoadingState(false);
          this.tryApplyPendingSelection();
        }),
        catchError((error) => {
          console.error(
            "[Statistics] Error loading deleted migrations:",
            error,
          );
          this.migrations = [];
          this.refreshTimelineView();
          this.changeLoadingState(false);
          return of([]);
        }),
      )
      .subscribe((result) => {
        console.log(
          "[Statistics] Deleted migrations subscribe completed with",
          result?.length || 0,
          "migrations",
        );
      });
  }

  private loadMigrationStatistics(migrationId: number): void {
    this.loading = true;
    this.error = false;
    this.statisticsLoadCompleted = false;
    this.migrationStore.setLoading(true);

    const migrationDetailsRequest =
      this.activeTabIndex === 1
        ? this.apiService.getDeletedMigrationDetails(migrationId)
        : this.apiService.getMigrationDetails(migrationId);

    migrationDetailsRequest
      .pipe(
        map((migration: Migration) =>
          this.migrationService.processMigrationRecord(migration),
        ),
        tap((processedMigration: Migration) => {
          this.migration = processedMigration;
          this.statisticsRows =
            this.migrationService.processMigrationStatisticsRows(
              processedMigration,
              "Z",
            );
          this.nameFilterOptions = Array.from(
            new Set(
              this.statisticsRows
                .map((row) => (row.name || "").trim())
                .filter((name) => !!name),
            ),
          ).sort((a, b) => a.localeCompare(b));
          this.selectedNameFilter = null;
          this.protocolIdFilterOptions = Array.from(
            new Set(
              this.statisticsRows
                .map((row) => row.protocol_id)
                .filter((id) => id != null && id !== "")
                .map((id) => (typeof id === "string" ? parseInt(id, 10) : id))
                .filter((id) => typeof id === "number" && !isNaN(id)),
            ),
          ).sort((a, b) => a - b);
          this.selectedProtocolIdFilter = null;
          this.statisticsPaginatorFilter.currentPage = 0;
          this.applyStatisticsPagination();
          this.updateDiagramChart();
          this.loading = false;
          this.statisticsLoadCompleted = true;
          this.migrationStore.setLoading(false);
        }),
        catchError(() => {
          this.error = true;
          this.loading = false;
          this.statisticsLoadCompleted = true;
          this.migration = null;
          this.statisticsRows = [];
          this.statisticsRowsPaginated = [];
          this.nameFilterOptions = [];
          this.selectedNameFilter = null;
          this.protocolIdFilterOptions = [];
          this.selectedProtocolIdFilter = null;
          this.statisticsPaginatorFilter.count = 0;
          this.migrationStore.setLoading(false);
          return of(null);
        }),
      )
      .subscribe();
  }

  getMigrationDisplayLabel(migration: Migration): string {
    const createdDate = (migration.created_dt || "")
      .split("T")[0]
      .split(" ")[0];

    return `${migration.id} - ${migration.company_name} - (${createdDate})`;
  }

  getSelectedMigrationDisplayLabel(): string {
    const selectedMigration = this.migrations.find(
      (migration) => migration.id === this.selectedMigrationId,
    );

    return selectedMigration
      ? this.getMigrationDisplayLabel(selectedMigration)
      : "-- None --";
  }

  private resetSelectedMigrationState(): void {
    this.selectedMigrationId = null;
    this.migrationId = null;
    this.migration = null;
    this.error = false;
    this.loading = false;
    this.statisticsLoadCompleted = false;
    this.statisticsRows = [];
    this.statisticsRowsPaginated = [];
    this.nameFilterOptions = [];
    this.selectedNameFilter = null;
    this.protocolIdFilterOptions = [];
    this.selectedProtocolIdFilter = null;
    this.migrationsViewMode = "table";
    this.deletedMigrationsViewMode = "table";
    this.statisticsPaginatorFilter.currentPage = 0;
    this.statisticsPaginatorFilter.count = 0;
  }

  private tryApplyPendingSelection(): void {
    if (!this.pendingQueryMigrationId) {
      return;
    }

    const matchingMigration = this.migrations.find(
      (migration) => migration.id === this.pendingQueryMigrationId,
    );

    if (!matchingMigration) {
      return;
    }

    this.selectedMigrationId = this.pendingQueryMigrationId;
    this.pendingQueryMigrationId = null;
    this.onMigrationSelectionChanged();
  }

  private applyStatisticsPagination(): void {
    // Sort the full dataset, then paginate
    const sorted = this.getSortedStatistics();
    const startIndex =
      this.statisticsPaginatorFilter.currentPage *
      this.statisticsPaginatorFilter.pageSize;
    const endIndex = startIndex + this.statisticsPaginatorFilter.pageSize;
    this.statisticsRowsPaginated = sorted.slice(startIndex, endIndex);
    this.statisticsPaginatorFilter.count = sorted.length;
  }

  private getFilteredStatistics(): MigrationStatisticsRow[] {
    const isDiagramView =
      this.activeTabIndex === 0
        ? this.migrationsViewMode === "diagram"
        : this.deletedMigrationsViewMode === "diagram";

    const hasNameFilter =
      !isDiagramView &&
      !!this.selectedNameFilter &&
      this.selectedNameFilter.trim() !== "";
    const hasProtocolIdFilter = this.selectedProtocolIdFilter !== null;

    if (!hasNameFilter && !hasProtocolIdFilter) {
      return [...this.statisticsRows];
    }

    return this.statisticsRows.filter((row) => {
      const rowProtocolId =
        typeof row.protocol_id === "string"
          ? parseInt(row.protocol_id, 10)
          : row.protocol_id;

      const nameMatch =
        !hasNameFilter || (row.name || "").trim() === this.selectedNameFilter;
      const protocolIdMatch =
        !hasProtocolIdFilter ||
        (typeof rowProtocolId === "number" &&
          !isNaN(rowProtocolId) &&
          rowProtocolId === this.selectedProtocolIdFilter);

      return nameMatch && protocolIdMatch;
    });
  }

  /** Return statistics rows sorted by the current sort state */
  private getSortedStatistics(): MigrationStatisticsRow[] {
    const dataToSort = this.getFilteredStatistics();

    if (!this.currentSort.active || this.currentSort.direction === "") {
      // No active sort — default to started ascending
      return [...dataToSort].sort(
        (a, b) =>
          new Date(a.startedSortValue || "").getTime() -
          new Date(b.startedSortValue || "").getTime(),
      );
    }

    const direction = this.currentSort.direction === "asc" ? 1 : -1;
    const active = this.currentSort.active;
    const sortType = this.columnSortTypes[active] || "string";

    let sortedData = [...dataToSort].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortType) {
        case "number":
          aVal = (a as any)[active] ?? 0;
          bVal = (b as any)[active] ?? 0;
          break;
        case "date":
          aVal = (a as any)[active]
            ? new Date((a as any)[active]).getTime()
            : 0;
          bVal = (b as any)[active]
            ? new Date((b as any)[active]).getTime()
            : 0;
          break;
        case "string":
        default:
          // For durationText, sort by underlying numeric duration
          if (active === "durationText") {
            aVal = a.duration ?? 0;
            bVal = b.duration ?? 0;
          } else {
            aVal = ((a as any)[active] || "").toString().toLowerCase();
            bVal = ((b as any)[active] || "").toString().toLowerCase();
          }

          break;
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
    return sortedData;
  }

  private changeLoadingState(state: boolean) {
    this.loading = state;
    this.loadingMigrations = state;
    this.migrationStore.setLoading(state);
  }

  /** Generate timeline chart data for diagram tab */
  private generateDiagramChart(): void {
    if (!this.migration || !this.migration.statistics_obj) {
      this.diagramChartData = [];
      this.initializingMigrationDuration = null;
      this.barChartData = {
        labels: [],
        datasets: [{ ...this.barChartData.datasets[0], data: [] }],
      };
      return;
    }

    const steps = this.migration.statistics_obj.steps || [];
    const descriptionMap = new Map<string, string>();
    this.getFilteredStatistics().forEach((row) => {
      const name = (row.name || "").trim();
      const description = (row.description || "").trim();

      if (name && description && !descriptionMap.has(name)) {
        descriptionMap.set(name, description);
      }
    });

    const durationMap = new Map<string, number>();

    steps.forEach((step: MigrationExecutionStep) => {
      if (step.name === "stage_company") {
        // For stage_company, get unique procedures by name and compute their durations
        const procedureMap = new Map<
          string,
          MigrationExecutionStoredProcedure[]
        >();

        if (step.store_procedures && step.store_procedures.length > 0) {
          const filteredProcedures =
            this.selectedProtocolIdFilter === null
              ? step.store_procedures
              : step.store_procedures.filter(
                  (proc: MigrationExecutionStoredProcedure) =>
                    proc.protocol_id === this.selectedProtocolIdFilter,
                );

          if (filteredProcedures.length === 0) {
            return;
          }

          filteredProcedures.forEach(
            (proc: MigrationExecutionStoredProcedure) => {
              const name = proc.name;
              if (!procedureMap.has(name)) {
                procedureMap.set(name, []);
              }
              procedureMap.get(name)!.push(proc);
            },
          );
        }

        // Create an entry for each unique procedure name
        procedureMap.forEach((procedures, procName) => {
          let proc_total_time = 0;
          procedures.forEach((p) => {
            if (p.started && p.ended) {
              proc_total_time +=
                new Date(p.ended).getTime() - new Date(p.started).getTime();
            }
          });

          durationMap.set(procName, proc_total_time);
        });
      } else {
        // Regular step: use started and ended times
        const stepStart = new Date(step.started || "").getTime();
        const stepEnd = new Date(step.ended || "").getTime();

        const stepDescription = (step.description || "").trim();
        if (step.name && stepDescription && !descriptionMap.has(step.name)) {
          descriptionMap.set(step.name, stepDescription);
        }

        if (stepStart && stepEnd) {
          const duration = stepEnd - stepStart;
          durationMap.set(
            step.name,
            (durationMap.get(step.name) || 0) + duration,
          );
        }
      }
    });

    this.initializingMigrationDuration = durationMap.get("initializing_migration") ?? null;
    durationMap.delete("initializing_migration");

    this.diagramChartData = Array.from(durationMap.entries())
      .map(([name, value]) => ({
        name,
        value,
        description: descriptionMap.get(name) || "",
      }))
      .sort((a, b) => b.value - a.value);

    this.barChartData = {
      labels: this.diagramChartData.map(
        (item) => `${item.name} (${this.migrationService.getDurationText(item.value)})`,
      ),
      datasets: [
        {
          data: this.diagramChartData.map((item) =>
            Number((item.value / 1000).toFixed(2)),
          ),
          label: "Duration (s)",
          backgroundColor: "#3b82f6",
          borderRadius: 6,
          borderWidth: 0,
        },
      ],
    };
  }

  /** Update diagram when migration is loaded */
  private updateDiagramChart(): void {
    if (this.migration) {
      this.generateDiagramChart();
    }
  }

  private refreshTimelineView(): void {
    if (!this.timelineViewEnabled || this.activeTabIndex !== 0) {
      this.timelineMigrations = [];
      this.updateTimelineChart();
      return;
    }

    this.loadingMigrations = true;

    this.apiService
      .getMigrations({
        startDatetime: this.selectedTimelineStartDate.toISOString(),
        endDatetime: this.selectedTimelineEndDate.toISOString(),
        ignoreColumns: [],
      })
      .pipe(
        map((migrations: Migration[]) =>
          migrations.map((migration) =>
            this.migrationService.processMigrationRecord(migration),
          ),
        ),
        tap((processedMigrations: Migration[]) => {
          this.timelineMigrations = processedMigrations.sort(
            (a, b) => (b.id || 0) - (a.id || 0),
          );
          this.updateTimelineChart();
          this.loadingMigrations = false;
        }),
        catchError((error) => {
          console.error("[Statistics] Error loading timeline migrations:", error);
          this.timelineMigrations = [];
          this.updateTimelineChart();
          this.loadingMigrations = false;
          return of([]);
        }),
      )
      .subscribe();
  }

  private updateTimelineChart(): void {
    this.timelineChartItems = this.timelineMigrations
      .map((migration) => this.buildTimelineChartItem(migration))
      .filter((item): item is TimelineChartItem => item !== null)
      .sort((left, right) => left.startHour - right.startHour);

    this.timelineBarChartData = {
      labels: this.timelineChartItems.map((item) => item.label),
      datasets: [
        {
          data: this.timelineChartItems.map((item) => [item.startHour, item.endHour]),
          label: "Migration window",
          backgroundColor: this.timelineChartItems.map(() => "#1670FE"),
          borderColor: this.timelineChartItems.map(() => "#0052CC"),
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false,
          barThickness: 18,
        },
      ],
    };
  }

  private buildTimelineChartItem(migration: Migration): TimelineChartItem | null {
    const statisticsStart =
      migration.statistics_obj?.steps?.find((step) => step.name === "copy_metadata")
        ?.started || migration.statistics_obj?.started;
    const statisticsEnd = migration.statistics_obj?.ended;

    let start = this.parseDisplayDate(statisticsStart);
    let end = this.parseDisplayDate(statisticsEnd);

    // Backward-compatible fallback for records that only include localized fields.
    if (!start) {
      start = this.parseDisplayDate(migration.statistics_started_local);
    }
    if (!end) {
      end = this.parseDisplayDate(migration.statistics_ended_local);
    }

    const hasStatistics = !!start && !!end;

    if (!start) {
      // No statistics — fall back to created_dt (normalized ISO string)
      start = migration.created_dt ? new Date(migration.created_dt) : null;
      if (!start || isNaN(start.getTime())) {
        return null;
      }
    }

    if (!end || end.getTime() <= start.getTime()) {
      // Show a minimal 5-minute bar so the migration is visible on the timeline
      end = new Date(start.getTime() + 5 * 60 * 1000);
    }

    const rangeStart = new Date(this.selectedTimelineStartDate);
    rangeStart.setHours(0, 0, 0, 0);

    const rangeEnd = new Date(this.selectedTimelineEndDate);
    rangeEnd.setHours(23, 59, 59, 999);

    const clampedStart = start < rangeStart ? rangeStart : start;
    const clampedEnd = end > rangeEnd ? rangeEnd : end;
    const durationMs = clampedEnd.getTime() - clampedStart.getTime();

    if (durationMs <= 0) {
      return null;
    }

    const fullLabel = `#${migration.id} ${migration.company_name}`;
    const truncatedLabel =
      fullLabel.length > 50 ? `${fullLabel.slice(0, 50)}...` : fullLabel;
    const startedLabel = hasStatistics
      ? this.migrationService.formatDateForDisplay(
          statisticsStart || migration.statistics_started_local,
          "Z",
        )
      : start.toLocaleString();
    const endedLabel = hasStatistics
      ? this.migrationService.formatDateForDisplay(
          statisticsEnd || migration.statistics_ended_local,
          "Z",
        )
      : end.toLocaleString();

    return {
      label: truncatedLabel,
      companyName: migration.company_name,
      migrationId: migration.id,
      startHour: this.getHourPosition(clampedStart),
      endHour: this.getHourPosition(clampedEnd),
      durationMs,
      startedLabel,
      endedLabel,
      hasStatistics,
    };
  }

  private parseDisplayDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  }

  private getHourPosition(date: Date): number {
    return (
      date.getHours() +
      date.getMinutes() / 60 +
      date.getSeconds() / 3600 +
      date.getMilliseconds() / 3600000
    );
  }


}
